// Câmera: posição (centro da vista, em px de arte) e zoom inteiro (px de tela por px
// de arte). Zoom inteiro mantém o pixel art nítido. Puro: testável em Node.

export interface Viewport {
  /** Tamanho da vista em px de tela (já com devicePixelRatio). */
  w: number;
  h: number;
}

export const MIN_ZOOM = 1;
/** Só a visão geral (botão O) desce abaixo de 1: zoom fracionário, desenhado sem suavizar. */
export const OVERVIEW_MIN_ZOOM = 0.2;
export const MAX_ZOOM = 8;

export class Camera {
  x = 0;
  y = 0;
  zoom = 3;
  /** Limites do mundo (px de arte); a câmera não sai deles. */
  bounds: { x: number; y: number; w: number; h: number } | null = null;

  /** Origem da vista em px de tela: onde o (0,0) do mundo cai. Arredondado para não borrar. */
  origin(view: Viewport): { ox: number; oy: number } {
    return { ox: Math.round(view.w / 2 - this.x * this.zoom), oy: Math.round(view.h / 2 - this.y * this.zoom) };
  }

  toScreen(wx: number, wy: number, view: Viewport): { x: number; y: number } {
    const { ox, oy } = this.origin(view);
    return { x: ox + wx * this.zoom, y: oy + wy * this.zoom };
  }

  toWorld(sx: number, sy: number, view: Viewport): { x: number; y: number } {
    const { ox, oy } = this.origin(view);
    return { x: (sx - ox) / this.zoom, y: (sy - oy) / this.zoom };
  }

  /** Arrasta a vista em px de tela. */
  pan(dxScreen: number, dyScreen: number): void {
    this.x -= dxScreen / this.zoom;
    this.y -= dyScreen / this.zoom;
    this.clamp();
  }

  /** Muda o zoom mantendo o ponto (sx, sy) da tela parado. */
  zoomAt(delta: number, sx: number, sy: number, view: Viewport): void {
    this.zoomEm(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round(this.zoom + delta))), sx, sy, view);
  }

  /** Zoom contínuo (fracionário) durante a pinça, mantendo (sx, sy) parado. Abaixo de 1 só na visão geral. */
  zoomLivre(zoom: number, sx: number, sy: number, view: Viewport): void {
    this.zoomEm(Math.max(OVERVIEW_MIN_ZOOM, Math.min(MAX_ZOOM, zoom)), sx, sy, view);
  }

  /** Ao soltar a pinça: arredonda para o zoom inteiro (pixel art nítido), no mesmo ponto. Abaixo de 1 fica como está. */
  fechaZoom(sx: number, sy: number, view: Viewport): void {
    if (this.zoom < MIN_ZOOM) return;
    this.zoomEm(Math.round(this.zoom), sx, sy, view);
  }

  private zoomEm(next: number, sx: number, sy: number, view: Viewport): void {
    if (next === this.zoom) return;
    const before = this.toWorld(sx, sy, view);
    this.zoom = next;
    const after = this.toWorld(sx, sy, view);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
    this.clamp();
  }

  /** Enquadra um retângulo do mundo inteiro na vista. */
  fit(rect: { x: number; y: number; w: number; h: number }, view: Viewport, margin = 16): void {
    const zx = view.w / (rect.w + margin * 2);
    const zy = view.h / (rect.h + margin * 2);
    this.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.floor(Math.min(zx, zy))));
    this.x = rect.x + rect.w / 2;
    this.y = rect.y + rect.h / 2;
  }

  /** Visão geral: enquadra o retângulo inteiro, mesmo que precise de zoom fracionário abaixo de 1. */
  fitOverview(rect: { x: number; y: number; w: number; h: number }, view: Viewport, margin = 16): void {
    const z = Math.min(view.w / (rect.w + margin * 2), view.h / (rect.h + margin * 2));
    // cabe com zoom inteiro: usa o maior inteiro (nítido); senão, o fracionário que cabe
    this.zoom = z >= MIN_ZOOM ? Math.min(MAX_ZOOM, Math.floor(z)) : Math.max(OVERVIEW_MIN_ZOOM, z);
    this.x = rect.x + rect.w / 2;
    this.y = rect.y + rect.h / 2;
  }

  private clamp(): void {
    if (!this.bounds) return;
    const b = this.bounds;
    this.x = Math.max(b.x, Math.min(b.x + b.w, this.x));
    this.y = Math.max(b.y, Math.min(b.y + b.h, this.y));
  }
}
